import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, HttpStatusCode } from "axios";
import { baseUrl, ApiEndpoints } from "../../config/constants";
import { userStore } from "../../app/stores/User";
import { configService } from "../config.service";
import { getPrefixedStorageName } from "../../utils/storagePrefix";


class ApiClient {
    private client: AxiosInstance;
    private refreshPromise: Promise<string | null> | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: baseUrl,
            timeout: 10000,
        });

        this.setupInterceptors();
    }

    private getStoreSlug(): string | null {
        // Extract subdomain directly from current URL (most reliable)
        const hostname = window.location.hostname;
        
        // Local development with subdomain
        if (hostname.endsWith('.localhost')) {
            return hostname.split('.')[0];
        }
        
        // Cloudflare Pages: Handle *.pages.dev domains
        // Examples: demo.cosmo-template.pages.dev -> "demo"
        if (hostname.endsWith('.pages.dev')) {
            const parts = hostname.split('.');
            
            // If it's subdomain.cosmo-template.pages.dev (4 parts), extract the subdomain
            if (parts.length === 4) {
                return parts[0];
            }
            
            // If it's just cosmo-template.pages.dev (3 parts), no subdomain - return null
            return null;
        }
        
        // Production subdomain (e.g., "demo.cosmocart.shop" -> "demo")
        const parts = hostname.split('.');
        if (parts.length >= 3) {
            return parts[0];
        }
        
        // Fallback to cached config
        const store = configService.getStore();
        return store?.subdomain || null;
    }

    private setupInterceptors() {
        // Request interceptor to add auth token and store context
        this.client.interceptors.request.use(
            (config) => {
                const user = userStore.getState().user;
                
                // Add store context header to ALL requests
                const storeSlug = this.getStoreSlug();
                if (storeSlug) {
                    config.headers['x-store-slug'] = storeSlug;
                }
                
                if (user.authenticated && user.session?.access_token) {
                    config.headers.Authorization = `Bearer ${user.session.access_token}`;
                    
                    // Check token expiry
                    const expiresAt = user.session?.expires_at;
                    if (expiresAt) {
                        const timeUntilExpiry = expiresAt - Math.floor(Date.now() / 1000);
                        if (timeUntilExpiry < 300) { // Less than 5 minutes
                            console.warn(`⏰ [ApiClient] Token expiring soon: ${timeUntilExpiry}s remaining for ${config.url}`);
                        }
                    }
                } else {
                    console.debug(`🔓 [ApiClient] Request: ${config.method?.toUpperCase()} ${config.url} (no auth, store: ${storeSlug})`);
                }
                
                return config;
            },
            (error) => {
                console.error("❌ [ApiClient] Request Error:", error);
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle token refresh
        this.client.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                const originalRequest = error.config;
                const status = error.response?.status;
                const url = originalRequest?.url;
                
                console.warn(`⚠️ [ApiClient] Request failed: ${originalRequest?.method?.toUpperCase()} ${url} → Status: ${status}`);
                
                // Check if it's an auth error (401 only, NOT 403) and we haven't already tried to refresh
                // 403 = Forbidden (user is authenticated but lacks permissions) - should NOT trigger token refresh
                // 401 = Unauthorized (invalid/expired token) - should trigger token refresh
                if (status === HttpStatusCode.Unauthorized && !originalRequest._retry) {
                    console.log(`🔄 [ApiClient] Auth error detected (${status}), attempting token refresh for: ${url}`);
                    originalRequest._retry = true;
                    
                    try {
                        const newToken = await this.refreshToken();
                        if (newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            return this.client(originalRequest);
                        } else {
                            console.error(`❌ [ApiClient] Token refresh returned null, logging out user`);
                            this.handleAuthError();
                            return Promise.reject(new Error("Token refresh failed"));
                        }
                    } catch (refreshError) {
                        console.error("❌ [ApiClient] Token refresh error:", refreshError);
                        // If refresh fails, clear session and redirect to login
                        this.handleAuthError();
                        return Promise.reject(refreshError);
                    }
                }
                
                // Log errors (403 = permission denied, others = general errors)
                if (status === 403) {
                    console.error(`🚫 [ApiClient] Permission denied (403): ${url}`, {
                        message: 'User lacks required permissions for this action',
                        data: error.response?.data
                    });
                } else if (status !== HttpStatusCode.Unauthorized) {
                    console.error(`❌ [ApiClient] Request error: ${url}`, {
                        status,
                        statusText: error.response?.statusText,
                        data: error.response?.data
                    });
                }
                
                // If not an auth error or refresh failed, reject the original error
                return Promise.reject(error);
            }
        );
    }

    private async refreshToken(): Promise<string | null> {
        // If refresh is already in progress, wait for it
        if (this.refreshPromise) {
            console.log('⏳ [ApiClient] Token refresh already in progress, waiting...');
            return this.refreshPromise;
        }

        const user = userStore.getState().user;
        if (!user.session?.refresh_token) {
            console.error('❌ [ApiClient] No refresh token available in session');
            return null;
        }

        console.log('🔄 [ApiClient] Starting token refresh process...');
        this.refreshPromise = this.performTokenRefresh(user.session.refresh_token);
        
        try {
            const newToken = await this.refreshPromise;
            if (newToken) {
                console.log('✅ [ApiClient] Token refresh completed successfully');
            } else {
                console.error('❌ [ApiClient] Token refresh returned null');
            }
            return newToken;
        } finally {
            this.refreshPromise = null;
        }
    }

    // Public method to manually refresh token (useful for preemptive refresh)
    public async manualRefreshToken(): Promise<boolean> {
        try {
            const newToken = await this.refreshToken();
            return !!newToken;
        } catch (error) {
            console.error("❌ Manual token refresh failed:", error);
            return false;
        }
    }

    private async performTokenRefresh(refreshToken: string): Promise<string | null> {
        const timestamp = new Date().toISOString();
        try {
            // Call our API's refresh endpoint instead of using Supabase client directly
            const response = await axios.post(ApiEndpoints.Auth.Refresh, {
                refresh_token: refreshToken
            });

            const { access_token, refresh_token: newRefreshToken } = response.data.data;
            
            if (!access_token) {
                console.error("❌ [ApiClient] No access_token in refresh response:", response.data);
                return null;
            }
            
            console.log(`✅ [ApiClient] Received new access token, updating session...`);
            
            // Update the stored session
            const currentUser = userStore.getState().user;
            const newExpiresAt = Math.floor(Date.now() / 1000) + 3600; // Add 1 hour expiry
            
            const updatedSession = {
                ...currentUser.session,
                access_token,
                refresh_token: newRefreshToken || refreshToken,
                expires_at: newExpiresAt,
            };

            console.log(`📝 [ApiClient] Session updated:`, {
                userId: currentUser.info?.id,
                email: currentUser.info?.email,
                newExpiresAt: new Date(newExpiresAt * 1000).toISOString(),
                hasNewRefreshToken: !!newRefreshToken
            });

            userStore.getState().updateUser({
                ...currentUser,
                session: updatedSession,
                authenticated: true, // Ensure user stays authenticated
            });

            // Update localStorage with proper prefix for multi-tenant support
            const sessionKey = getPrefixedStorageName("session");
            localStorage.setItem(sessionKey, JSON.stringify(updatedSession));

            return access_token;
        } catch (error: any) {
            console.error("❌ [ApiClient] Token refresh failed:", {
                timestamp,
                url: ApiEndpoints.Auth.Refresh,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message,
                code: error.code
            });
            
            // Check if it's a refresh token expiry (400/401 from refresh endpoint)
            if (error.response?.status === 400 || error.response?.status === 401) {
                console.log("🔐 [ApiClient] Refresh token expired or invalid, user needs to re-authenticate");
            } else if (error.code === "ECONNREFUSED" || error.code === "ERR_NETWORK") {
                console.log("🌐 [ApiClient] Network error during token refresh - API server may be down");
            } else {
                console.log("❓ [ApiClient] Unknown error during token refresh");
            }
            
            return null;
        }
    }

    private handleAuthError() {
        const timestamp = new Date().toISOString();
        const currentUser = userStore.getState().user;
        
        console.error(`🚪 [ApiClient] ${timestamp} - Logging out user due to auth error`, {
            userId: currentUser.info?.id,
            email: currentUser.info?.email,
            wasAuthenticated: currentUser.authenticated,
            currentPath: window.location.pathname
        });
        
        // Clear user store directly (no hooks needed)
        userStore.getState().updateUser({
            info: {} as any,
            session: {} as any,
            authenticated: false,
            role: "user" as any
        });
        
        // Clear localStorage with proper prefix for multi-tenant support
        const userKey = getPrefixedStorageName("user");
        const sessionKey = getPrefixedStorageName("session");
        localStorage.removeItem(userKey);
        localStorage.removeItem(sessionKey);
        
        console.log('🗑️ [ApiClient] Cleared user session from store and localStorage');
        
        // Call the API signout endpoint (optional, for server-side cleanup)
        try {
            axios.post(`${baseUrl}/auth/signout`).catch(() => {
                console.log('⚠️ [ApiClient] API signout call failed (ignoring)');
            });
        } catch (error) {
            console.log('⚠️ [ApiClient] Error calling API signout (ignoring)');
        }
        
        // Only redirect if not already on an auth page
        const currentPath = window.location.pathname;
        const authPaths = ["/login", "/register", "/portal/login"];
        
        if (!authPaths.includes(currentPath)) {
            setTimeout(() => {
                window.location.href = "/login";
            }, 100);
        } else {
            console.log(`ℹ️ [ApiClient] Already on auth page (${currentPath}), no redirect needed`);
        }
    }

    // Public methods for making requests
    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.get(url, config);
    }

    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.post(url, data, config);
    }

    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.put(url, data, config);
    }

    public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.patch(url, data, config);
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.delete(url, config);
    }

    // Public method for making requests without auth (for public endpoints)
    public async getPublic<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const storeSlug = this.getStoreSlug();
        const publicClient = axios.create({
            baseURL: baseUrl,
            timeout: 10000,
            headers: {
                ...(storeSlug && { 'x-store-slug': storeSlug }),
            },
        });
        return publicClient.get(url, config);
    }

    public async postPublic<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const storeSlug = this.getStoreSlug();
        const publicClient = axios.create({
            baseURL: baseUrl,
            timeout: 10000,
            headers: {
                ...(storeSlug && { 'x-store-slug': storeSlug }),
            },
        });
        return publicClient.post(url, data, config);
    }
}

// Export a singleton instance
export const apiClient = new ApiClient();
export default apiClient; 